// Pointing an incident at somebody: **sharing** it (asking a colleague to look,
// while whoever holds it goes on holding it) or **reallocating** it (handing it
// over). One dialog for both, because they are the same decision one step apart and
// were two different-shaped controls — a button and a dialog for one, an inline
// picker inside the form for the other — which made the pair harder to use than
// either alone.
//
// Both do the same three things: choose a person or one of the event's teams, tick
// whether to text them a link (on by default), and edit the wording if the default
// doesn't say enough. **Neither needs the text**: an unticked box shares or
// reallocates exactly as before and simply tells nobody, for when they're standing
// next to you or on the radio. A share still grants the recipient sight of that one
// incident either way (models/incident_share.py) — that grant, not the SMS, is what
// a share is.
//
// The default message comes from the server (it knows the actor and the front end's
// own address) and is only sent back when the sender rewrote it, so an untouched one
// is rebuilt from the incident as saved rather than as the dialog found it. See
// core/incident_shares.py.
//
// Persisting is the caller's job (`onSubmit`): a share is its own endpoint, a
// reallocation is a PUT of the incident, and the incident dialog already knows how
// to build one. This component owns the choice, not the write.
import { useEffect, useState } from "react";
import axios from "axios";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText,
  Alert, Button, Stack, TextField, MenuItem, ListSubheader, Typography, Box,
  Checkbox, FormControlLabel, CircularProgress,
} from "@mui/material";
import ShareIcon from "@mui/icons-material/Share";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import { API_BASE_URL } from "../config";
import { sortByAttendanceRole, surnameKey } from "../constants";

const INCIDENTS_API = `${API_BASE_URL}/incidents`;

// What each action is called, and how it reads. `action` is also the server's own
// vocabulary for the default message (GET /share-message?action=…).
const ACTIONS = {
  share: {
    title: "Share this incident",
    blurb: "Lets whoever you share it with open this incident. The allocation "
           + "doesn't change, and nothing else on the event becomes visible to "
           + "them — just this incident, for as long as they are on the event.",
    pickerLabel: "Share with",
    submit: "Share",
    notifyLabel: "Text them a link to this incident",
    icon: <ShareIcon />,
  },
  allocate: {
    title: "Reallocate this incident",
    blurb: "Hands the incident to someone else on the event, or to one of its "
           + "teams — an incident allocated to a team is every member's.",
    pickerLabel: "Allocate to",
    submit: "Reallocate",
    notifyLabel: "Text them a link to this incident",
    icon: <AssignmentIndIcon />,
  },
};

const fullName = (p) => `${p.forename || ""} ${p.surname || ""}`.trim();
const memberNames = (members) => members.map(fullName).join(", ");

// One person's option. A single line: what used to be its second — the warning that
// they can't be texted — is now said once, as the heading of the group they sit in.
const personItem = (p) => (
  <MenuItem key={`p:${p.id}`} value={`p:${p.id}`}>
    {p.name}{p.role ? ` — ${p.role}` : ""}
  </MenuItem>
);


/**
 * Props:
 *  - open, onClose, action        "share" | "allocate"
 *  - incidentId
 *  - attendances, teams           the incident's event, as the page already loaded
 *  - creator                      { name } for the allocate action's "back to the
 *                                 creator" option; omitted for a share
 *  - currentValue                 the allocate action's present target, so picking
 *                                 it again can't be submitted as a change
 *  - onSubmit({ personnelId, teamId, notify, message }) => Promise
 */
export default function IncidentTargetDialog({
  open, onClose, action = "share", incidentId, attendances, teams,
  creator, currentValue, onSubmit,
}) {
  const spec = ACTIONS[action] || ACTIONS.share;
  const allocating = action === "allocate";

  // "p:<id>" for a person, "t:<id>" for a team, "creator" for the incident's
  // creator — three id spaces in one dropdown, prefixed to keep them apart.
  const [target, setTarget] = useState("");
  const [notify, setNotify] = useState(true);
  const [message, setMessage] = useState("");
  const [defaultMessage, setDefaultMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !incidentId) return;
    setTarget("");
    setNotify(true);
    setLoading(true);
    axios.get(`${INCIDENTS_API}/${incidentId}/share-message`, { params: { action } })
      .then((res) => {
        setDefaultMessage(res.data?.message || "");
        setMessage(res.data?.message || "");
      })
      .catch(() => { setDefaultMessage(""); setMessage(""); })
      .finally(() => setLoading(false));
  }, [open, incidentId, action]);

  // `can_view_incidents` is the server's own verdict (AttendanceRead), not a rule
  // restated here: it unions the person's *global* roles with their attendance, so
  // someone who is Clinical Manager globally but **Mountain Safety** on this event
  // is correctly still counted. A role check here would wrongly exclude them.
  //
  // The two actions differ on what to do about someone who can't open incidents.
  // A share is refused outright by the server (it would grant nothing), so they
  // aren't offered. An allocation is allowed — somebody has to be able to record
  // who is dealing with a casualty on the hill — so they are offered, in a group of
  // their own at the bottom under a heading that says once what they have in common.
  const teamList = teams || [];
  const all = (attendances || [])
    .filter((a) => a.person)
    .map((a) => ({ id: a.personnel_id, name: fullName(a.person), role: a.role,
                   surnameKey: surnameKey(a.person), canOpen: !!a.can_view_incidents }))
    // Read by role then surname, as the event's own personnel list is: whoever is
    // looking for a colleague here is thinking of their job first.
    .sort(sortByAttendanceRole);
  const reachable = all.filter((p) => p.canOpen);
  const unreachable = allocating ? all.filter((p) => !p.canOpen) : [];
  // In dropdown order, which is also what `person` below is looked up in.
  const people = [...reachable, ...unreachable];
  // Only label the groups when there is more than one to tell apart.
  const grouped = teamList.length > 0 || unreachable.length > 0;

  // A team's members arrive as PersonnelSummary and carry no eligibility of their
  // own, so it's read off the attendances this dialog already has — the same list
  // the People group is built from. Matches the server's _team_recipients().
  const eligibleIds = new Set(all.filter((p) => p.canOpen).map((p) => p.id));
  const recipients = (t) => (t?.members || []).filter((m) => eligibleIds.has(m.id));

  const team = target.startsWith("t:")
    ? teamList.find((t) => t.id === Number(target.slice(2))) || null
    : null;
  const person = target.startsWith("p:")
    ? people.find((p) => `p:${p.id}` === target) || null
    : null;

  // Whether anyone at this target could open a link, and so whether offering to
  // text one means anything. Reallocating back to the creator assumes yes: they
  // raised the incident, and the snapshot carries only their email — the server is
  // what decides who is actually texted.
  const canText = team ? recipients(team).length > 0
    : person ? person.canOpen
      : target === "creator";
  // A share needs a target who can open it; the server refuses one that can't, so
  // an all-ineligible team is a dead end rather than a silent no-text.
  const deadEnd = !allocating && !!team && recipients(team).length === 0;
  const unchanged = allocating && !!target && target === currentValue;
  const canSubmit = !!target && !deadEnd && !unchanged && !busy
    && (!notify || !canText || !!message.trim());

  const submit = () => {
    setBusy(true);
    const edited = message.trim();
    Promise.resolve(onSubmit({
      personnelId: target.startsWith("p:") ? Number(target.slice(2)) : null,
      teamId: target.startsWith("t:") ? Number(target.slice(2)) : null,
      // Never ask for a text the dialog didn't offer: the server would drop the
      // recipient anyway, but the request shouldn't claim otherwise.
      notify: notify && canText,
      // Only an edited message is sent — see the note at the top.
      message: edited && edited !== defaultMessage.trim() ? edited : null,
    }))
      .then(() => onClose())
      .catch(() => {}) // the global interceptor surfaces the error
      .finally(() => setBusy(false));
  };

  const targetLabel = () => {
    if (team) return `${team.name} (team)`;
    if (person) return person.name;
    return target === "creator" && creator?.name ? `${creator.name} (creator)` : "";
  };

  return (
    <Dialog open={open} onClose={() => !busy && onClose()} fullWidth maxWidth="sm">
      <DialogTitle>{spec.title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <DialogContentText>{spec.blurb}</DialogContentText>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <>
              <TextField label={spec.pickerLabel} select fullWidth value={target}
                         onChange={(e) => setTarget(e.target.value)}
                         // A team's option is two lines (name + who's in it), so the
                         // closed field renders its own one-line summary instead.
                         slotProps={{ select: { renderValue: targetLabel } }}
                         helperText={deadEnd
                           ? "No one in this team can open this incident, so there "
                             + "is nobody to share it with."
                           : team ? memberNames(recipients(team)) || " " : " "}
                         error={deadEnd}>
                {allocating && (
                  <MenuItem value="creator">
                    {creator?.name ? `${creator.name} (creator)` : "Creator"}
                  </MenuItem>
                )}
                {/* Teams first: it's usually the broader choice. */}
                {teamList.length > 0 && <ListSubheader>Teams</ListSubheader>}
                {teamList.map((t) => (
                  <MenuItem key={`t:${t.id}`} value={`t:${t.id}`}>
                    <Box>
                      <Typography variant="body2">{t.name}</Typography>
                      <Typography variant="caption" color="text.secondary"
                                  sx={{ display: "block" }}>
                        {/* An allocation reaches the whole team, a share's text
                            only the members who can open it. */}
                        {(allocating ? memberNames(t.members || []) : memberNames(recipients(t)))
                          || "No one who can open this incident"}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
                {grouped && reachable.length > 0 && <ListSubheader>People</ListSubheader>}
                {reachable.map(personItem)}
                {/* Said once, over the group, rather than under every name: on an
                    event with a dozen Mountain Safety attendees the repeated caption
                    was most of the list. They can still be allocated the incident —
                    the heading explains what they have in common, which is only that
                    no text will reach them. */}
                {unreachable.length > 0 && (
                  // Longer than the other two headings, and the menu is narrow on a
                  // phone: let it wrap instead of being clipped by the default
                  // single-line height.
                  <ListSubheader sx={{ whiteSpace: "normal", lineHeight: 1.5, py: 1 }}>
                    These can't open incidents — won't be texted
                  </ListSubheader>
                )}
                {unreachable.map(personItem)}
              </TextField>

              {unchanged && (
                <Alert severity="info">
                  This incident is already allocated there.
                </Alert>
              )}

              {/* The act is allowed, only the text isn't. Say which, so it doesn't
                  read as the allocation being refused. */}
              {!!target && !canText && !deadEnd && (
                <Alert severity="info">
                  {team
                    ? "No one in this team can open incidents on this event, so no "
                      + "text will be sent. The allocation still saves."
                    : "This person's role on the event gives them no access to "
                      + "incidents, so they can't open a link to it and no text will "
                      + "be sent. The allocation still saves."}
                </Alert>
              )}

              {canText && (
                <Box>
                  <FormControlLabel
                    control={<Checkbox checked={notify}
                                       onChange={(e) => setNotify(e.target.checked)} />}
                    label={spec.notifyLabel} />
                  {notify ? (
                    <TextField label="Message" fullWidth multiline minRows={3}
                               sx={{ mt: 1 }}
                               value={message}
                               onChange={(e) => setMessage(e.target.value)}
                               helperText={`${message.length} characters, sent by SMS. `
                                           + "Edit it if you want to say more than the default."} />
                  ) : (
                    <Typography variant="caption" color="text.secondary"
                                sx={{ display: "block" }}>
                      {allocating
                        ? "They won't be told — the incident is still handed over, "
                          + "and the change is recorded on it either way."
                        : "They won't be told — they can still open the incident, "
                          + "and the share is recorded on it either way."}
                    </Typography>
                  )}
                </Box>
              )}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" disabled={!canSubmit} onClick={submit}
                startIcon={busy ? <CircularProgress size={16} /> : spec.icon}>
          {spec.submit}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
